import React from 'react';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useCreateTag, useUpdateTag } from '@juno/client-api';
import { Site, Tag, TagType } from '@juno/client-api/model';
import { DrawerFooter, DrawerHeader } from '@juno/ui';
import { FEATURE_FLAG, MutationAction, onMutation, useFeatureFlagWithUser } from '@juno/utils';

interface TagFormProps {
  site: Site;
  tags?: Tag[];
  tagTypes?: TagType[];
  handleCancelEdit: () => void;
  refetch: () => void;
  initialValues?: Tag;
}

const validationSchema = (allTags: Tag[], initialValues?: Tag) => {
  return Yup.object().shape({
    value: Yup.string()
      .max(90, 'Name must not exceed 90 characters')
      .required('Name is required')
      .test('title-unique-validation', 'Tag value must be unique', function test(value) {
        if (value && value.length > 0) {
          if (
            allTags &&
            allTags.filter(
              (t) => t.value.toLowerCase() === value.toLowerCase() && t.id !== initialValues?.id,
            ).length > 0
          ) {
            return false;
          }
          return true;
        } else {
          return true;
        }
      }),
    type: Yup.object().required(),
  });
};

const TagForm: React.FC<TagFormProps> = ({
  site,
  tags,
  tagTypes,
  handleCancelEdit,
  refetch,
  initialValues,
}) => {
  const { value: directoryTagFiltersValue } = useFeatureFlagWithUser(
    FEATURE_FLAG.DIRECTORY_TAG_FILTERS,
  );

  const createTag = useCreateTag(onMutation(MutationAction.CREATE, 'Tag', refetch));
  const updateTag = useUpdateTag(onMutation(MutationAction.UPDATE, 'Tag', refetch));

  const handleCreateEditTag = async (
    formValues: Tag,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setSubmitting(true);
    try {
      if (formValues.id) {
        await updateTag.mutateAsync({
          siteId: site?.id || '',
          tagId: formValues.id,
          data: { ...formValues },
        });
      } else {
        await createTag.mutateAsync({
          siteId: site?.id || '',
          data: {
            ...formValues,
          },
        });
      }
      handleCancelEdit();
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  const defaultInitialValue = directoryTagFiltersValue
    ? {}
    : { type: tagTypes?.find((tt) => tt.value == 'Global Declared') };

  return (
    <Formik
      initialValues={initialValues || (defaultInitialValue as Tag)}
      onSubmit={async (formValues, { setSubmitting }) => {
        await handleCreateEditTag(formValues, setSubmitting);
      }}
      validationSchema={validationSchema(tags || [], initialValues)}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        isValid,
        errors,
        touched,
        setTouched,
        dirty,
        isSubmitting,
        resetForm,
        handleSubmit,
      }) => {
        return (
          <Form>
            <Box>
              <TextField
                size='small'
                value={values.value}
                name='name'
                label='Tag Name'
                fullWidth
                error={Boolean(touched.value && errors.value)}
                helperText={touched.value && errors.value}
                onChange={(e) => {
                  setTouched({ value: true });
                  setFieldValue('value', e.target.value);
                }}
              />
              <FormControl fullWidth size='small' sx={{ mt: 3 }}>
                <InputLabel id='select-tag-type-label'>Select Tag Type</InputLabel>
                <Select
                  fullWidth
                  size='small'
                  label='Select Tag Type'
                  name='type'
                  value={values.type?.id || ''}
                  error={Boolean(touched.type && errors.type)}
                  disabled={!directoryTagFiltersValue}
                  onChange={(e) => {
                    setTouched({ type: { id: true } });
                    setFieldValue(
                      'type',
                      tagTypes?.find((tt) => tt.id === e.target.value),
                    );
                  }}
                  inputProps={{
                    'aria-label': 'Select Tag Type',
                    'aria-labelledby': 'select-tag-type-label',
                  }}
                >
                  {tagTypes?.map((tt) => (
                    <MenuItem key={tt.id} value={tt.id}>
                      {tt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormHelperText>{touched.type?.id && errors.type}</FormHelperText>
            </Box>
            <DrawerFooter
              disabled={!isValid || !dirty}
              submitting={isSubmitting}
              onCancel={() => {
                handleCancelEdit();
                resetForm();
              }}
              onSubmit={() => handleSubmit()}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default TagForm;
